/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import { CompatRoute, CompatRouter } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import { Tooltip } from 'brand-guidelines/Tooltip-styled-components';
import { GetAnonymousSessionData } from 'connect/saga/handlers/anonymous-session/get-anonymous-session-data';
import { ForcedExperimentVariantsStateHandler } from 'connect/state/handlers/experiments';
import ErrorPage from 'containers/ErrorPage/Loadable';
import FunnelPage from 'containers/FunnelPage/Loadable';
import NotAuthorizedPage from 'containers/NotAuthorizedPage/Loadable';
import NotFoundPage from 'containers/NotFoundPage/Loadable';
import PartnerPage from 'containers/PartnerPage/Loadable';
import PricingPage from 'containers/PricingPage/Loadable';
import ProfilePage from 'containers/ProfilePage/Loadable';
import { ResultsPageLoadable } from 'containers/ResultsPage/Loadable';
import { CheckoutPage } from 'pages/checkout/CheckoutPage';
import { getParam } from 'pages/common/helpers/url-helpers';
import { EXPERIMENTS } from 'utils/constants/experiment-configs';
import { useIsPartnerUser } from 'utils/customHooks/use-partner-user';
import injectSaga from 'utils/injectSaga';

import CONFIG from '../../../app.config';
import { requestUserAndPatient } from '../../connect/actions';
import { GetExperimentVariants } from '../../connect/saga/handlers/experiment/get-experiment-variants';
import saga from '../../connect/saga/saga-watcher';
import GlobalStyle from '../../global-styles';
import { useGoogleOneTap } from '../../services/googleOneTap/useGoogleOneTap';
import ConciergePage from '../ConciergePage/Loadable';

import RouteListener from './RouteListener';
import {
  CONCIERGE_PATH,
  ERROR_PATH,
  NOT_AUTHORIZED_PATH,
  PARTNER_PATH,
  PRICING_PATH,
  RECOMMENDATION_PATH,
  RESULTS_PATH,
} from './Routes';

const App = () => {
  const dispatch = useDispatch();

  const isPartnerUser = useIsPartnerUser();

  const location = useLocation();
  const { search } = location;

  // Initialize Google One Tap for the entire application
  useGoogleOneTap();

  // Load initial data from the API
  useEffect(() => {
    dispatch(GetExperimentVariants.createAction());
    dispatch(GetAnonymousSessionData.createAction());

    // It is important to request the user and patient when the app initializes because
    // loadingUser defaults to true in the reducer and is blacklisted from persistence.
    // If this call is removed, loadingUser will remain true indefinitely, which would
    // prevent features that wait for the user state (like Google One Tap) from initializing.
    dispatch(requestUserAndPatient());
  }, [dispatch]);

  useEffect(() => {
    const experimentNames = Object.values(EXPERIMENTS).map(
      (experiment) => experiment.name,
    );
    const requestedExperimentVariants = getParam(search, ...experimentNames);

    const experimentVariants = requestedExperimentVariants.reduce(
      (acc, variant, index) => {
        if (!variant) {
          return acc;
        }

        const experimentName = experimentNames[index]!;
        return {
          ...acc,
          [experimentName]: variant,
        };
      },
      {},
    );

    // Update forced experiments even if no variants are given
    // This will clear out any previously forced experiments
    dispatch(ForcedExperimentVariantsStateHandler.update(experimentVariants));
  }, [dispatch, search]);

  return (
    <>
      <Helmet>
        <title>Opencare - Find a Dentist Near You</title>
        <base href={CONFIG.SEARCH_APP_V2_URL} />
      </Helmet>
      <BrowserRouter>
        <CompatRouter>
          <RouteListener>
            <Switch>
              <Route exact path="/">
                <Redirect to={RESULTS_PATH} />
              </Route>

              <Route path={RECOMMENDATION_PATH}>
                {isPartnerUser ? <Redirect to={PARTNER_PATH} /> : <FunnelPage />}
              </Route>

              <Route exact path={RESULTS_PATH}>
                {isPartnerUser ? <Redirect to={PARTNER_PATH} /> : <ResultsPageLoadable />}
              </Route>

              <Route exact path={PRICING_PATH}>
                {isPartnerUser ? <Redirect to={PARTNER_PATH} /> : <PricingPage />}
              </Route>

              <Route
                exact
                path={[
                  PARTNER_PATH,
                  `${PARTNER_PATH}/:clinicId`,
                  `${PARTNER_PATH}/:clinicId/:providerId`,
                ]}
              >
                <PartnerPage />
              </Route>

              <Route path={CONCIERGE_PATH}>
                {isPartnerUser ? <Redirect to={PARTNER_PATH} /> : <ConciergePage />}
              </Route>

              <CompatRoute path={`${RESULTS_PATH}/:clinicId/checkout`}>
                <CheckoutPage />
              </CompatRoute>

              <Route path={`${RESULTS_PATH}/:clinicId(\\d+)`}>
                <ProfilePage />
              </Route>

              <Route path={NOT_AUTHORIZED_PATH}>
                <NotAuthorizedPage />
              </Route>

              <Route path={ERROR_PATH}>
                <ErrorPage />
              </Route>

              <Route>
                <NotFoundPage />
              </Route>
            </Switch>
          </RouteListener>
        </CompatRouter>
      </BrowserRouter>
      <GlobalStyle />
      {/* For optimal performance, react-tooltip should always be rendered in the root component */}
      <Tooltip id="react-tooltip" />
    </>
  );
};

const withSaga = injectSaga({ key: 'rootSaga', saga });

export default compose(withSaga)(App);
